import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ListTodo, Clock4, Layers3, CheckCircle2, Users, Briefcase } from "lucide-react";
import Layout from "../components/Layout";
import KpiCard from "../components/KpiCard";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const STAGE_PILL = {
  "New": "bg-blue-50 text-blue-700",
  "Market Qualified Lead (MQL)": "bg-cyan-50 text-cyan-700",
  "Sales Qualified Lead (SQL)": "bg-sky-50 text-sky-700",
  "Junk": "bg-slate-100 text-slate-600",
  "Not connected": "bg-slate-100 text-slate-700",
  "Followup/Connect later": "bg-indigo-50 text-indigo-700",
  "Requirement Gathering(RFI)": "bg-amber-50 text-amber-700",
  "Solutioning / Proposal Preparation": "bg-amber-100 text-amber-800",
  "Proposal Submission": "bg-orange-50 text-orange-700",
  "Negotiation": "bg-orange-100 text-orange-800",
  "Agreement Signing": "bg-orange-200 text-orange-900",
  "Won": "bg-emerald-50 text-emerald-700",
  "Onboarding": "bg-emerald-100 text-emerald-800",
  "Live": "bg-emerald-200 text-emerald-900",
  "On Hold": "bg-violet-50 text-violet-700",
  "Lost": "bg-rose-50 text-rose-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [pickable, setPickable] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get("/reports/summary"),
          api.get("/leads?pickable=true&limit=10"),
        ]);
        setSummary(s.data);
        setPickable(p.data?.items || []);
      } catch {}
    })();
  }, []);

  return (
    <Layout
      title={`Welcome back, ${(user?.name || "User").split(" ")[0]}!`}
      breadcrumb={["Home", "Dashboard"]}
    >
      {/* Hero card */}
      <div className="peach-bg border border-orange-100 rounded-2xl p-6 mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-orange-700 font-semibold">
            Lead Management System
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1 tracking-tight">
            Today’s pipeline at a glance
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Track new captures, qualify your hot leads, and convert faster with disciplined stage SOPs.
          </p>
        </div>
        <Link
          to="/leads?create=1"
          data-testid="dashboard-add-lead-btn"
          className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2.5 rounded-md flex items-center gap-1.5 shadow-sm"
        >
          <Plus size={14} /> Add a lead
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Link to="/leads" className="block h-full">
          <KpiCard
            testid="kpi-total-leads"
            title="Total leads"
            value={summary?.total_leads ?? "—"}
            hint={`This month: ${summary?.new_this_month ?? 0}`}
            accent="orange"
          />
        </Link>
        <Link to="/leads?recent=true" className="block h-full">
          <KpiCard
            testid="kpi-new-leads"
            title="New leads"
            value={summary?.new_leads_recent ?? "—"}
            hint="Added this month or lead age ≤ 20 days"
            accent="blue"
          />
        </Link>
        <Link to="/leads?in_pipeline=true" className="block h-full">
          <KpiCard
            testid="kpi-in-pipeline"
            title="In pipeline"
            value={summary?.in_pipeline ?? "—"}
            hint="Active opportunities"
            accent="violet"
          />
        </Link>
        <Link to="/leads?lead_stage=Won" className="block h-full">
          <KpiCard
            testid="kpi-won-accounts"
            title="Won Leads"
            value={summary?.won_accounts ?? "—"}
            hint="Won"
            accent="green"
          />
        </Link>
      </div>

      {/* Expected revenue by late-stage pipeline step (kept separate, not summed together) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {(summary?.revenue_by_stage || []).map((r) => (
          <Link key={r.stage} to={`/leads?lead_stage=${encodeURIComponent(r.stage)}`} className="block h-full">
            <KpiCard
              testid={`kpi-revenue-${r.stage.toLowerCase().replace(/\s+/g, "-")}`}
              title={`${r.stage} — Expected revenue`}
              value={`₹${Number(r.expected_revenue || 0).toLocaleString()}`}
              hint="Sum of expected_revenue for leads in this stage"
              accent="orange"
            />
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Action items + Pipeline grid */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Pipeline overview</h3>
              <Link to="/leads" className="text-xs text-orange-600 hover:underline">View all</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {(summary?.by_stage || []).filter((s) => s.stage !== "Live").map((s) => (
                <Link
                  key={s.stage}
                  to={`/leads?lead_stage=${encodeURIComponent(s.stage)}`}
                  data-testid={`pipeline-tile-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block rounded-xl p-3 border border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all"
                >
                  <div className={`text-2xl font-bold ${STAGE_PILL[s.stage]?.split(" ").pop() || "text-slate-900"}`}>
                    {s.count}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-tight">{s.stage}</div>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Today’s action items</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ActionTile
                icon={<ListTodo size={18} />}
                label="New leads to call"
                value={summary?.new_leads ?? 0}
                color="bg-emerald-50 text-emerald-700"
                to="/leads?lead_stage=New"
              />
              <ActionTile
                icon={<Clock4 size={18} />}
                label="Pickable leads"
                value={pickable.length}
                color="bg-orange-50 text-orange-700"
                to="/leads?pickable=true"
              />
              <ActionTile
                icon={<Layers3 size={18} />}
                label="My open leads"
                value={summary?.my_leads ?? 0}
                color="bg-blue-50 text-blue-700"
                to="/leads?mine=true"
              />
            </div>
          </div>
        </div>

        {/* Right column: pickable list + head office breakdown */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm h-fit">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Pickable leads</h3>
              <Link to="/leads?pickable=true" className="text-xs text-orange-600 hover:underline">
                See all
              </Link>
            </div>
            {pickable.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">No pickable leads right now.</div>
            )}
            <ul className="space-y-2.5">
              {pickable.slice(0, 6).map((l) => (
                <li key={l.id}>
                  <Link
                    to={`/leads/${l.id}`}
                    data-testid={`pickable-row-${l.id}`}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-md hover:bg-slate-50 border border-slate-100"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{l.company_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {l.lead_stage} • Last owner: {l.lead_owner_name || "—"}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                      Pick
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Leads by head office of client */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm h-fit">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Leads by head office of client</h3>
            {(summary?.leads_by_head_office || []).length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No head office data yet.</div>
            ) : (
              <ul className="space-y-2">
                {(summary?.leads_by_head_office || []).map((r) => (
                  <li key={r.office}>
                    <Link
                      to={`/leads?client_head_office=${encodeURIComponent(r.office)}`}
                      data-testid={`head-office-row-${r.office.toLowerCase().replace(/\s+/g, "-")}`}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all"
                    >
                      <span className="text-sm text-slate-700 truncate">{r.office}</span>
                      <span className="text-sm font-semibold text-slate-900 tabular-nums">{r.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Tag counts per sales owner/executive */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mt-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Tags by sales owner</h3>
        {(summary?.tags_by_owner || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No tagged leads yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500">Sales owner</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Hot</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Warm</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Cold</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Deal Closed</th>
                  <th className="text-right py-2 pl-3 text-xs uppercase tracking-wider text-slate-500">Lost</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.tags_by_owner || []).map((row) => {
                  const tagHref = (tag) => {
                    const qp = new URLSearchParams({ stage_tagging: tag });
                    if (row.owner_id) qp.set("lead_owner_id", row.owner_id);
                    return `/leads?${qp.toString()}`;
                  };
                  return (
                    <tr key={row.owner_id || row.owner} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4 font-medium text-slate-800">{row.owner}</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        <Link to={tagHref("Hot")} className="text-rose-600 hover:underline">{row["Hot"]}</Link>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        <Link to={tagHref("Warm")} className="text-amber-600 hover:underline">{row["Warm"]}</Link>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        <Link to={tagHref("Cold")} className="text-sky-600 hover:underline">{row["Cold"]}</Link>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        <Link to={tagHref("Deal Closed")} className="text-emerald-700 hover:underline">{row["Deal Closed"]}</Link>
                      </td>
                      <td className="py-2 pl-3 text-right tabular-nums">
                        <Link to={tagHref("Lost")} className="text-slate-500 hover:underline">{row["Lost"]}</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead age distribution */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mt-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Lead age</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {(summary?.leads_by_age || []).map((b) => {
            const range = AGE_BUCKET_RANGES[b.bucket];
            const qp = new URLSearchParams({ age_min: String(range?.min ?? 0) });
            if (range?.max != null) qp.set("age_max", String(range.max));
            return (
              <Link
                key={b.bucket}
                to={`/leads?${qp.toString()}`}
                className="block rounded-xl p-3 border border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all"
              >
                <div className="text-2xl font-bold text-slate-900">{b.count}</div>
                <div className="text-[11px] text-slate-500 mt-1 leading-tight">{b.bucket}</div>
              </Link>
            );
          })}
        </div>

        {/* Per-owner breakdown — whose leads are how old */}
        {(summary?.age_by_owner || []).length > 0 && (
          <div className="overflow-x-auto mt-5 pt-5 border-t border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500">Sales owner</th>
                  {Object.keys(AGE_BUCKET_RANGES).map((label) => (
                    <th key={label} className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(summary?.age_by_owner || []).map((row) => (
                  <tr key={row.owner_id || row.owner} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-4 font-medium text-slate-800">{row.owner}</td>
                    {Object.entries(AGE_BUCKET_RANGES).map(([label, range]) => {
                      const qp = new URLSearchParams({ age_min: String(range.min) });
                      if (range.max != null) qp.set("age_max", String(range.max));
                      if (row.owner_id) qp.set("lead_owner_id", row.owner_id);
                      return (
                        <td key={label} className="py-2 px-3 text-right tabular-nums">
                          <Link to={`/leads?${qp.toString()}`} className="text-slate-700 hover:underline">{row[label]}</Link>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

const AGE_BUCKET_RANGES = {
  "0-7 days": { min: 0, max: 7 },
  "8-15 days": { min: 8, max: 15 },
  "16-30 days": { min: 16, max: 30 },
  "31-60 days": { min: 31, max: 60 },
  "60+ days": { min: 61, max: null },
};

function ActionTile({ icon, label, value, color, to }) {
  return (
    <Link
      to={to}
      data-testid={`action-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-3 p-4 rounded-lg border border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all"
    >
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${color}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-bold text-slate-900">{value}</div>
      </div>
    </Link>
  );
}
